import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import plutonicLogo from "../assets/images/plutonicLogo.svg";
import plutonicSmallName from "../assets/images/plutonicSmallName.svg";
import { useNavigate } from "react-router-dom";
import Env from "../runtimeEnv";

function LoginForm({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupChoice, setShowSignupChoice] = useState(false);
  const navigate = useNavigate();

  // ✅ Use LOCAL loading state instead of AuthContext loading
  const [localLoading, setLocalLoading] = useState(false);

  const {
    setCurrentNickname,
    setPlutonicuserID,
    setIsAuthenticated,
    setUserType,
    currentFormType,
    setCurrentFormType,
    // Remove loading and setLoading from AuthContext usage
    showNewPasswordForm,
    setShowNewPasswordForm,
    setSurveyCompleted,
    surveyCompleted,
    setEmail: setEmailContext,
    verifyUserAccessFromServer,
  } = useContext(AuthContext);

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUppercase: false,
    hasLowercase: false,
  });

  useEffect(() => {
    const requirements = {
      minLength: /^(?=.{8,}).*$/,
      hasNumber: /^(?=.*[0-9]).*$/,
      hasSpecialChar: /^(?=.*[!@#$%^&*]).*$/,
      hasUppercase: /^(?=.*[A-Z]).*$/,
      hasLowercase: /^(?=.*[a-z]).*$/,
    };

    setPasswordRequirements({
      minLength: requirements.minLength.test(newPassword),
      hasNumber: requirements.hasNumber.test(newPassword),
      hasSpecialChar: requirements.hasSpecialChar.test(newPassword),
      hasUppercase: requirements.hasUppercase.test(newPassword),
      hasLowercase: requirements.hasLowercase.test(newPassword),
    });
  }, [newPassword]);

  const randomNicknames = [
    "Mind Explorer",
    "Growth Seeker",
    "Healing Hero",
    "Journey Navigator",
    "Balance Warrior",
    "Insight Chaser",
    "Calm Creator",
    "Resilience Builder",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true); // ✅ Use local loading
    setError(""); // Clear any previous errors

    const trimmedPassword = password.trim();

    try {
      const response = await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/login`,
        {
          email,
          password: trimmedPassword,
        },
        { withCredentials: true }
      );

      if (response.data.newTemporaryPasswordSent) {
        setError(
          "Your temporary password has expired. A new one has been sent to your email."
        );
        setLocalLoading(false);
        return;
      }

      if (response.data.challenge === "NEW_PASSWORD_REQUIRED") {
        setShowNewPasswordForm(true);
        setLocalLoading(false);
        return;
      } else {
        const { PlutonicuserID, nickname, userType, surveyCompleted, email } =
          response.data;

        let assignedNickname =
          nickname ||
          randomNicknames[Math.floor(Math.random() * randomNicknames.length)];

        if (!nickname) {
          try {
            await axios.put(
              `${Env.REACT_APP_PYTHON_API_URL}/api/update-nickname`,
              {
                PlutonicuserID,
                newNickname: assignedNickname,
              },
              { withCredentials: true }
            );
          } catch (error) {
            console.error("LoginForm.js: error event");
          }
        }

        setCurrentNickname(assignedNickname);
        setPlutonicuserID(PlutonicuserID || "unknown");
        setUserType(userType || "user");
        setIsAuthenticated(true);
        setSurveyCompleted(surveyCompleted);
        setEmailContext(email);

        localStorage.setItem("currentNickname", assignedNickname);
        localStorage.setItem("PlutonicuserID", PlutonicuserID || "unknown");
        localStorage.setItem("userType", userType || "user");
        localStorage.setItem("surveyCompleted", surveyCompleted);
        localStorage.setItem("email", email);

        if (verifyUserAccessFromServer) {
          await verifyUserAccessFromServer();
        }

        if (surveyCompleted) {
          navigate("/"); // main area
        } else {
          navigate("/survey"); // ask survey
        }

        setLocalLoading(false);
        onLoginSuccess(
          assignedNickname,
          PlutonicuserID,
          userType || "unknown",
          surveyCompleted
        );
      }
    } catch (err) {
      console.error("LoginForm.js: error event");

      setLocalLoading(false); // ✅ Stop local loading

      if (
        err.response?.status === 401 &&
        err.response?.data?.challenge === "NEW_PASSWORD_REQUIRED"
      ) {
        setShowNewPasswordForm(true);
      } else {
        const errorMessage =
          err.response?.data?.message ||
          "Login failed. Please check your credentials.";
        setError(errorMessage);
      }
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true); // ✅ Use local loading
    setError("");

    const trimmedNewPassword = newPassword.trim();

    try {
      const response = await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/set_new_password`,
        {
          email,
          newPassword: trimmedNewPassword,
        },
        { withCredentials: true }
      );

      if (response.data.message === "Password updated successfully") {
        const {
          PlutonicuserID,
          nickname,
          userType,
          email: responseEmail,
          surveyCompleted,
        } = response.data;

        let assignedNickname =
          nickname ||
          randomNicknames[Math.floor(Math.random() * randomNicknames.length)];

        if (!nickname) {
          try {
            await axios.put(
              `${Env.REACT_APP_PYTHON_API_URL}/api/update-nickname`,
              {
                PlutonicuserID,
                newNickname: assignedNickname,
              },
              { withCredentials: true }
            );
          } catch (error) {
            console.error("LoginForm.js: error event");
          }
        }

        setCurrentNickname(assignedNickname);
        localStorage.setItem("nickname", assignedNickname);
        localStorage.setItem("PlutonicuserID", PlutonicuserID || "unknown");
        localStorage.setItem("userType", userType || "user");
        localStorage.setItem("surveyCompleted", surveyCompleted || "false");
        localStorage.setItem("email", responseEmail || email);

        setSurveyCompleted(surveyCompleted || false);
        setEmailContext(responseEmail || email);

        onLoginSuccess(
          assignedNickname,
          PlutonicuserID,
          userType || "unknown",
          surveyCompleted
        );
      } else {
        setError("Failed to set password. Please try again.");
      }
    } catch (err) {
      console.error("LoginForm.js: error event");
      setError(err.response?.data?.message || "Failed to set new password.");
    } finally {
      setLocalLoading(false); // ✅ Use local loading
    }
  };

  const preventSpace = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  return (
    <div className="bg-black bg-opacity-80 p-8 rounded-3xl shadow-lg w-[350px] text-white text-center">
      {showNewPasswordForm ? (
        <>
          <h2 className="text-2xl font-bold mb-6">Set New Password</h2>
          <form onSubmit={handleNewPasswordSubmit} className="space-y-2">
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value.trim())}
                onKeyDown={preventSpace}
                placeholder="New Password"
                className="w-full p-3 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-gray-400"
                onClick={() => setShowNewPassword((prev) => !prev)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <div className="text-left small-text mt-2">
              <p>Password must meet the following requirements:</p>
              <ul className="list-disc ml-5 mt-1">
                <li
                  className={
                    passwordRequirements.minLength
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  Minimum length: 8 characters
                </li>
                <li
                  className={
                    passwordRequirements.hasNumber
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  Contains at least 1 number
                </li>
                <li
                  className={
                    passwordRequirements.hasSpecialChar
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  Contains at least 1 special character
                </li>
                <li
                  className={
                    passwordRequirements.hasUppercase
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  Contains at least 1 uppercase letter
                </li>
                <li
                  className={
                    passwordRequirements.hasLowercase
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  Contains at least 1 lowercase letter
                </li>
              </ul>
            </div>

            <button
              type="submit"
              className="w-full py-3 border border-[#22AAEE] text-[#22AAEE] font-bold rounded-full transition duration-200 hover:bg-[#22AAEE] hover:text-white"
              disabled={localLoading}
            >
              {localLoading ? "Setting Password..." : "Submit"}
            </button>
          </form>
          {/* ✅ Move error outside form and make it more prominent */}
          {error && (
            <div className="mt-4 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg">
              <p className="text-red-400 text-sm font-medium">⚠️ {error}</p>
            </div>
          )}
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold mb-6">Login</h2>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                placeholder="Email"
                className="w-full p-3 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
                required
              />
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value.trim())}
                onKeyDown={preventSpace}
                placeholder="Password"
                className="w-full p-3 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
                required
              />
              <button
                type="button"
                className="absolute inset-y-0 right-3 flex items-center text-gray-400"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-3 border border-[#22AAEE] text-[#22AAEE] font-bold rounded-full transition duration-200 hover:bg-[#22AAEE] hover:text-white"
              disabled={localLoading}
            >
              {localLoading ? "Signing in..." : "ENTER"}
            </button>
          </form>

          {/* Error message for login form */}
          {error && (
            <div className="mt-4">
              <p className="text-red-500">{error}</p>
            </div>
          )}

          <div className="mt-4 space-y-2">
            <p
              className="text-sm text-left cursor-pointer hover:underline text-gray-400"
              onClick={() => {
                setCurrentFormType("reset");
                localStorage.setItem("currentFormType", "reset");
              }}
            >
              Reset Password?
            </p>
          </div>
        </>
      )}
      <div className="mt-2 flex items-center justify-center space-x-2">
        <img
          src={plutonicLogo}
          alt="Plutonic Logo"
          className="w-[50px] h-[30px]"
        />
        <img src={plutonicSmallName} alt="company name" className="w-20 h-20" />
      </div>
    </div>
  );
}

export default LoginForm;
